import React, { useEffect, useState } from 'react';

import {
  Breakpoints,
  CloseButton,
  CTA,
  InputAppearanceValue,
  LayerWrapper,
  ShimLayer,
  TextColor,
  TextCustom,
  TextLineHeight,
  TextSize,
  TextWeight,
} from '@vw-marketing/us-components';

import { NoTranslateTextInput } from '../../../shared/components/no-translate-text-input';
import { StyledLayerHeader } from '../../../shared/components/layer-content-wrapper/styles';
import {
  StyledContentWrapper,
  StyledCtaWrapper,
  StyledModalContent,
  StyledPinVerificationWrapper,
  StyledResetPinLink,
} from './styles';
import {
  onVWBasicLayerLoadEvent,
  onVWBasicButtonClickEvent,
  onVWBasicLinkClickEvent,
  onVWBasicGenericErrorMessageLoadEvent,
} from '../../../shared/tagging/tagging-helpers';
import { formatLockDuration } from '../../../shared/utils/format-lock-duration';
import { resetPinLayerRender } from '../layer-manager/resetPinLayerRender';
import { useAuth } from '../../../app/providers/AuthContext';
import { useFeatureServices } from '../../../shared/hooks/use-feature-services';
import { useToast } from '../../../shared/context/ToastContext';
import { useMyVwContext } from '../../../shared/hooks/use-my-vw-context';
import { useResetPinLayer } from '../layer-manager/useResetPinLayer';
import { useTrackingManager } from '../../../shared/hooks/use-tracking-manager';

interface PinVerificationModalProps {
  challenge: string;
  isLocked: boolean;
  lockDuration?: string;
  onSuccess: () => void;
}

export const PinVerificationModal: React.FC<PinVerificationModalProps> = ({
  challenge,
  isLocked,
  lockDuration,
  onSuccess,
}) => {
  const [pin, setPin] = useState<string>('');
  const [appearance, setAppearance] = useState<InputAppearanceValue>('default');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isTouched, setIsTouched] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isLockedDynamic, setIsLockedDynamic] = useState<boolean>(false);
  const [isResetPinLayerOpen, setIsResetPinLayerOpen] = useState<boolean>(false);
  const pinRef = React.useRef<string>('');

  const { logout } = useAuth();
  const { authService, translationHandler } = useMyVwContext();
  const { showToast, hideToast } = useToast();
  const { 'locale-service': localeService } = useFeatureServices();
  const localeCode = `${localeService.language}-${localeService.countryCode}`;
  const isCanada = localeService.countryCode === 'CA';
  const trackingManager = useTrackingManager();

  useResetPinLayer(
    isResetPinLayerOpen,
    () => {
      setIsResetPinLayerOpen(false);
      setPin('');
      setAppearance('default');
      setErrorMessage('');
      setIsTouched(false);
    },
    resetPinLayerRender,
    {
      authService,
      translationHandler,
      trackingManager,
      localeCode,
      isCanada,
      onSuccess: () => setIsResetPinLayerOpen(false),
      onChange: () => {},
    },
  );

  useEffect(() => {
    try {
      onVWBasicLayerLoadEvent(
        trackingManager,
        'Enter your 4-digit PIN',
        localeService.countryCode,
        'Security Pin',
      );
    } catch {
      console.warn('Error tracking Security PIN Management layer load');
    }

    if (isLocked) {
      setAppearance('error');
      const msg = lockDuration
        ? translationHandler('securityPinManagement.input.error.lockedWithDuration').replace('{{duration}}', lockDuration)
        : translationHandler('securityPinManagement.input.error.locked');
      setErrorMessage(msg);
    }
  }, []);

  const handleChange = (e: React.SyntheticEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value;
    if (value === '' || /^\d*$/.test(value)) {
      pinRef.current = value;
      setPin(value);
      if (isTouched) {
        validateOnChange(value);
      }
    }
  };

  const handleBlur = () => {
    setIsTouched(true);
    const currentPin = pinRef.current;
    if (currentPin.length > 0 && currentPin.length < 4) {
      setAppearance('error');
      setErrorMessage(translationHandler('securityPinManagement.input.error.length'));
    } else if (currentPin.length === 0) {
      setAppearance('error');
      setErrorMessage(translationHandler('securityPinManagement.input.error.empty'));
    }
  };

  const validateOnChange = (value: string) => {
    if (value.length === 4) {
      setAppearance('default');
      setErrorMessage('');
    } else if (value.length > 0 && value.length < 4) {
      setAppearance('error');
      setErrorMessage(translationHandler('securityPinManagement.input.error.length'));
    } else if (value.length === 0) {
      setAppearance('error');
      setErrorMessage(translationHandler('securityPinManagement.input.error.empty'));
    }
  };

  const verifyPin = async () => {
    try {
      onVWBasicButtonClickEvent(
        trackingManager,
        { Name: translationHandler('securityPinManagement.cta.verify') },
        'Security Pin',
      );
    } catch {
      console.warn('Error tracking Verify your PIN click');
    }

    try {
      setIsSubmitting(true);
      setAppearance('default');
      setErrorMessage('');

      const response = await authService.validateUserSecurityPin(pin, challenge, localeCode);
      const validationStatus = response?.validateUserSecurityPin?.pinValidationStatus;

      if (validationStatus === 'SUCCESS') {
        hideToast();
        onSuccess();
      } else {
        const spinSeconds = response?.validateUserSecurityPin?.spinLockedWaitingTimeInSeconds;
        let errorMsg: string;
        if (spinSeconds) {
          setIsLockedDynamic(true);
          errorMsg = translationHandler('securityPinManagement.input.error.lockedWithDuration')
            .replace('{{duration}}', formatLockDuration(spinSeconds));
        } else {
          errorMsg = translationHandler('securityPinManagement.input.error.invalid');
        }
        setAppearance('error');
        setErrorMessage(errorMsg);

        try {
          onVWBasicGenericErrorMessageLoadEvent(
            trackingManager,
            {
              code: 'PIN_VALIDATION_FAILED',
              message: errorMsg,
              referringUrl: window.location.href,
            },
            'Security Pin',
          );
        } catch {
          console.warn('Error tracking PIN validation error');
        }
      }
    } catch {
      showToast(translationHandler('securityPinManagement.toast.error'), 'Alert');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetPin = () => {
    try {
      onVWBasicLinkClickEvent(
        trackingManager,
        { Name: translationHandler('securityPinManagement.cta.reset') },
        undefined,
        'Security Pin',
      );
    } catch {
      console.warn('Error tracking Reset PIN click');
    }

    setIsResetPinLayerOpen(true);
  };

  const locked = isLocked || isLockedDynamic;
  const isButtonDisabled = pin.length !== 4 || isSubmitting || locked;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isButtonDisabled) return;
    verifyPin();
  };

  const handlePinKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (isButtonDisabled) return;
      verifyPin();
    }
  };

  return (
    <LayerWrapper contentCentered>
      <ShimLayer />
      <form onSubmit={handleSubmit}>
        <StyledModalContent>
          <StyledLayerHeader>
            <TextCustom
              style={{
                [Breakpoints.default]: {
                  fontSize: TextSize.TextSize24,
                  fontWeight: TextWeight.bold,
                  color: TextColor.primary,
                  lineHeight: TextLineHeight.TextLineHeight28,
                },
                [Breakpoints.b1280]: {
                  fontSize: TextSize.TextSize32,
                  lineHeight: TextLineHeight.TextLineHeight36,
                },
              }}
            >
              {translationHandler('securityPinManagement.title')}
            </TextCustom>
            <CloseButton onClick={logout} />
          </StyledLayerHeader>

          <StyledContentWrapper>
            <StyledPinVerificationWrapper>
              <NoTranslateTextInput
                selectors={['p', 'span', 'input']}
                appearance={locked ? 'error' : appearance}
                aria-label={translationHandler('securityPinManagement.input.label')}
                aria-required="true"
                characterCountLimit={0}
                inputMode="numeric"
                label={translationHandler('securityPinManagement.input.label')}
                maxLength={4}
                message={errorMessage}
                name="pin"
                onBlur={locked ? undefined : handleBlur}
                onChange={locked ? undefined : handleChange}
                onKeyDown={locked ? undefined : handlePinKeyDown}
                required
                togglePasswordVisibilityIconLabel={translationHandler('securityPinManagement.toggle.visibility')}
                type="password"
                value={pin}
              />

              <StyledResetPinLink
                type="button"
                onClick={handleResetPin}
                data-testid="reset-pin-link"
              >
                <TextCustom
                  style={{
                    [Breakpoints.default]: {
                      fontSize: TextSize.TextSize16,
                      color: TextColor.primary,
                      fontWeight: TextWeight.light,
                      lineHeight: TextLineHeight.TextLineHeight24,
                    },
                  }}
                >
                  {translationHandler('securityPinManagement.cta.reset')} →
                </TextCustom>
              </StyledResetPinLink>
            </StyledPinVerificationWrapper>

            <StyledCtaWrapper>
              <CTA
                aria-label={translationHandler('securityPinManagement.cta.verify')}
                disabled={isButtonDisabled}
                emphasis="primary"
                tag="button"
                type="submit"
                data-testid="verify-pin-button"
              >
                {translationHandler('securityPinManagement.cta.verify')}
              </CTA>
            </StyledCtaWrapper>
          </StyledContentWrapper>
        </StyledModalContent>
      </form>
    </LayerWrapper>
  );
};
